import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Domain {
  id: string
  domain: string
  customer_id: string
  customer_name: string
  status: string
  ssl_status: string
  ssl_expires: string | null
  ssl_auto_renew: boolean
  dns_configured: boolean
  dns_records: { type: string; name: string; value: string; verified: boolean }[]
  created_at: string
  last_verified: string | null
}

interface Certificate {
  id: string
  domain: string
  issuer: string
  type: string
  issued_at: string
  expires_at: string
  status: string
  auto_renew: boolean
}

export default function CustomDomainsPage() {
  const [domains, setDomains] = useState<Domain[]>([])
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [domRes, certRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/custom-domains/'),
      fetch(API_BASE + '/custom-domains/certificates/'),
      fetch(API_BASE + '/custom-domains/statistics')
    ])
    if (domRes.ok) setDomains((await domRes.json()).domains || [])
    if (certRes.ok) setCertificates((await certRes.json()).certificates || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'valid': return 'bg-green-100 text-green-800'
      case 'pending_verification': case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'ssl_expiring': case 'expiring_soon': return 'bg-orange-100 text-orange-800'
      case 'error': case 'expired': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Custom Domain Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Domain</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Domains</div>
              <div className="text-2xl font-bold">{statistics.total_domains}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_domains}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_verification}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">SSL Valid</div>
              <div className="text-2xl font-bold text-green-600">{statistics.ssl_valid}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">SSL Expiring</div>
              <div className="text-2xl font-bold text-orange-600">{statistics.ssl_expiring_soon}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">DNS Issues</div>
              <div className="text-2xl font-bold text-red-600">{statistics.dns_issues}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Domains</h2></div>
            <div className="divide-y">
              {domains.map(domain => (
                <div key={domain.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedDomain(domain)}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{domain.domain}</div>
                      <div className="text-sm text-gray-500">{domain.customer_name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(domain.status)}`}>{domain.status.replace(/_/g, ' ')}</span>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(domain.ssl_status)}`}>SSL: {domain.ssl_status}</span>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span>DNS: {domain.dns_configured ? 'Configured' : 'Not configured'}</span>
                    {domain.ssl_expires && <span>SSL expires: {new Date(domain.ssl_expires).toLocaleDateString()}</span>}
                    {domain.ssl_auto_renew && <span className="text-green-600">Auto-renew</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {selectedDomain ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedDomain.domain}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Customer</span>
                    <span>{selectedDomain.customer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedDomain.status)}`}>{selectedDomain.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">SSL Status</span>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedDomain.ssl_status)}`}>{selectedDomain.ssl_status}</span>
                  </div>
                  {selectedDomain.ssl_expires && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">SSL Expires</span>
                      <span>{new Date(selectedDomain.ssl_expires).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Auto Renew</span>
                    <span>{selectedDomain.ssl_auto_renew ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="font-medium mb-2">DNS Records</div>
                    {selectedDomain.dns_records.map((rec, i) => (
                      <div key={i} className="p-2 bg-gray-50 rounded mb-2">
                        <div className="flex justify-between text-xs">
                          <span className="font-mono">{rec.type}</span>
                          <span className={rec.verified ? 'text-green-600' : 'text-yellow-600'}>{rec.verified ? 'Verified' : 'Pending'}</span>
                        </div>
                        <div className="text-xs text-gray-500 font-mono mt-1">{rec.name} → {rec.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Verify</button>
                    <button className="flex-1 px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700">Renew SSL</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Domain Details</h3>
                <p className="text-gray-500 text-sm">Select a domain to view details</p>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">SSL Certificates</h3>
              <div className="space-y-3">
                {certificates.map(cert => (
                  <div key={cert.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm">{cert.domain}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(cert.status)}`}>{cert.status}</span>
                    </div>
                    <div className="text-xs text-gray-500">{cert.issuer} - {cert.type}</div>
                    <div className="text-xs text-gray-400 mt-1">Expires: {new Date(cert.expires_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
